import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import ContactSection from "@/components/sections/ContactSection";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { fmtEUR } from "@/lib/finance";
import useSeo from "@/hooks/useSeo";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  Check, KeyRound, CalendarClock, ArrowRight, Zap,
  Truck, Tag, BadgeEuro, Headphones, Smartphone, BadgeCheck, Wallet, LifeBuoy, Quote, Gauge,
} from "lucide-react";

const GLANCE_ICONS = [Truck, CalendarClock, Tag, BadgeEuro, Headphones, Smartphone, Check, Check];
const ADVANTAGE_ICONS = [BadgeCheck, Wallet, LifeBuoy];
const BRAND_STRIP = ["BMW", "MERCEDES-BENZ", "VOLKSWAGEN", "AUDI", "PORSCHE", "VOLVO"];
const fmtKm = (n) => new Intl.NumberFormat("de-DE").format(n);

export default function AutoAbo() {
  const { t, lang } = useLang();
  const L = t.abo.listing;
  const [prefill, setPrefill] = useState(t.abo.title);
  const [aboOffers, setAboOffers] = useState([]);
  const [brandFilter, setBrandFilter] = useState("all");
  const [priceFilter, setPriceFilter] = useState("all");

  useEffect(() => window.scrollTo(0, 0), []);
  useEffect(() => {
    api.get("/offers", { params: { section: "auto-abo" } }).then(({ data }) => setAboOffers(data)).catch(() => {});
  }, []);

  const brands = useMemo(() => [...new Set(aboOffers.map((o) => o.brand).filter(Boolean))], [aboOffers]);
  const filtered = useMemo(
    () =>
      aboOffers.filter((o) => {
        if (brandFilter !== "all" && o.brand !== brandFilter) return false;
        if (priceFilter === "low" && !(o.price <= 1000)) return false;
        if (priceFilter === "mid" && !(o.price > 1000 && o.price <= 2000)) return false;
        if (priceFilter === "high" && !(o.price > 2000)) return false;
        return true;
      }),
    [aboOffers, brandFilter, priceFilter]
  );

  const jsonLd = useMemo(
    () =>
      aboOffers.length
        ? {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Auto Abo Angebote – DNA Automotive GmbH",
            itemListElement: aboOffers.map((o, i) => ({
              "@type": "ListItem",
              position: i + 1,
              item: {
                "@type": "Product",
                name: `${o.brand} ${o.title}`,
                brand: o.brand,
                ...(o.image ? { image: new URL(o.image, window.location.origin).href } : {}),
                offers: {
                  "@type": "Offer",
                  price: o.price,
                  priceCurrency: "EUR",
                  availability: "https://schema.org/InStock",
                  seller: { "@type": "AutoDealer", name: "DNA Automotive GmbH" },
                },
              },
            })),
          }
        : null,
    [aboOffers]
  );

  useSeo({
    title: "Auto-Abo Köln · 14 Fahrzeuge sofort verfügbar | DNA Automotive",
    description: "Exklusive Auto Abos in der Motorworld Köln: BMW, Mercedes-Benz, Audi, MINI, Range Rover u.v.m. sofort verfügbar. Transparente monatliche Bruttoraten ab 890 €, bis zu 3.500 Freikilometer monatlich.",
    path: "/auto-abo",
    breadcrumbs: [{ name: "Auto-Abo", url: "/auto-abo" }],
    jsonLd,
  });

  const scrollContact = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  const scrollListing = () => document.getElementById("abo-listing")?.scrollIntoView({ behavior: "smooth" });
  const inquire = (o) => {
    setPrefill(`Auto-Abo: ${o.brand} ${o.title}`);
    setTimeout(scrollContact, 60);
  };

  const chipCls = (active) =>
    `px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${
      active ? "bg-brand text-black border-brand" : "border-white/15 text-zinc-300 hover:border-brand-light hover:text-brand-bright"
    }`;

  return (
    <div className="bg-obsidian min-h-screen" data-testid="auto-abo-page">
      <Header />

      {/* Hero – clean, ohne Fahrzeugbilder */}
      <section className="relative pt-40 pb-24 px-6 md:px-10 overflow-hidden grain">
        <div className="absolute inset-0 z-0 bg-obsidian-surface" />
        <div className="absolute inset-0 z-0 bg-gradient-to-t from-obsidian via-obsidian/85 to-obsidian/60" />
        <div className="relative z-10 max-w-[1600px] mx-auto">
          <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-4">{t.abo.overline}</p>
          <h1 className="font-heading font-black uppercase tracking-tighter text-white text-5xl md:text-7xl leading-[0.9] mb-6">{t.abo.title}</h1>
          <p className="text-zinc-300 max-w-2xl text-lg mb-8">{t.abo.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button onClick={scrollContact} className="group inline-flex items-center justify-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-7 py-4 hover:bg-brand-hover transition-colors" data-testid="abo-hero-consult">
              {t.abo.ctaConsult}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={scrollListing} className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-bold uppercase tracking-wider text-sm px-7 py-4 hover:border-brand-light hover:text-brand-bright transition-colors" data-testid="abo-hero-explore">
              {t.abo.ctaExplore}
            </button>
          </div>
        </div>
      </section>

      {/* Auf einen Blick */}
      <section className="px-6 md:px-10 py-20 border-t border-white/10" data-testid="abo-glance">
        <div className="max-w-[1600px] mx-auto">
          <h2 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-5xl mb-3">{t.abo.glanceTitle}</h2>
          <p className="text-zinc-500 max-w-2xl mb-10 text-sm">{t.abo.glanceSubtitle}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {t.abo.glance.map((g, i) => {
              const Icon = GLANCE_ICONS[i] || Check;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}
                  className="bg-obsidian p-6 md:p-7"
                  data-testid={`abo-glance-${i}`}
                >
                  <Icon className="w-7 h-7 text-brand-bright mb-4" />
                  <div className="font-heading font-black text-white text-xl md:text-2xl leading-tight">{g.t}</div>
                  <div className="text-[12px] uppercase tracking-wider text-zinc-500 mt-1.5">{g.d}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ihre Vorteile */}
      <section className="px-6 md:px-10 pb-20" data-testid="abo-advantages">
        <div className="max-w-[1600px] mx-auto">
          <h2 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-5xl mb-10">{t.abo.advantagesTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.abo.advantages.map((a, i) => {
              const Icon = ADVANTAGE_ICONS[i] || Check;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="bg-obsidian-surface border border-white/10 p-8 hover:border-brand-light/40 transition-colors duration-500"
                  data-testid={`abo-advantage-${i}`}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-brand/15 border border-brand-light/30 mb-6">
                    <Icon className="w-6 h-6 text-brand-bright" />
                  </div>
                  <h3 className="font-heading font-extrabold uppercase tracking-tight text-white text-lg mb-3 leading-snug">{a.t}</h3>
                  <p className="text-zinc-400 leading-relaxed text-sm">{a.d}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* So funktioniert's */}
      <section className="px-6 md:px-10 py-20 border-t border-white/10 bg-obsidian-surface" data-testid="abo-steps">
        <div className="max-w-[1600px] mx-auto">
          <h2 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-5xl mb-3">{t.abo.stepsTitle}</h2>
          <p className="text-zinc-500 max-w-2xl mb-12 text-sm">{t.abo.stepsSubtitle}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.abo.steps.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="relative border-t-2 border-brand-light pt-6"
                data-testid={`abo-step-${i}`}
              >
                <span className="font-heading font-black text-brand-bright/25 text-6xl leading-none block mb-4">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-heading font-extrabold uppercase tracking-tight text-white text-lg mb-3 leading-snug">{s.t}</h3>
                <p className="text-zinc-400 leading-relaxed text-sm">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Marken-Leiste (reine Typografie, keine Fahrzeugdarstellung) */}
      <section className="w-full overflow-hidden border-y border-white/10 py-10 bg-obsidian" data-testid="abo-brands">
        <p className="text-center text-brand-bright text-xs uppercase tracking-[0.3em] mb-6">{t.abo.brandsTitle}</p>
        <div className="flex whitespace-nowrap animate-marquee">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex items-center shrink-0">
              {BRAND_STRIP.map((b, i) => (
                <span key={`${dup}-${i}`} className="flex items-center">
                  <span className="font-heading font-black uppercase tracking-tight text-4xl md:text-6xl text-stroke-white px-8">{b}</span>
                  <span className="text-brand-bright text-2xl md:text-4xl">/</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Sofort verfügbare Auto-Abos */}
      <section id="abo-listing" className="px-6 md:px-10 py-24 md:py-28" data-testid="abo-listing-section">
        <div className="max-w-[1600px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-4">{L.overline}</p>
            <h2 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-5xl leading-[0.95] mb-6" data-testid="abo-listing-title">
              {L.title}
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-4">{L.text}</p>
            <p className="text-zinc-400 leading-relaxed">{L.text2}</p>
          </motion.div>

          {/* Filter */}
          <div className="mt-12 mb-10 space-y-4" data-testid="abo-filters">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setBrandFilter("all")} className={chipCls(brandFilter === "all")} data-testid="abo-filter-brand-all">
                {L.allBrands}
              </button>
              {brands.map((b) => (
                <button key={b} onClick={() => setBrandFilter(b)} className={chipCls(brandFilter === b)} data-testid={`abo-filter-brand-${b.toLowerCase().replace(/\s+/g, "-")}`}>
                  {b}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => setPriceFilter("all")} className={chipCls(priceFilter === "all")} data-testid="abo-filter-price-all">
                {L.allPrices}
              </button>
              <button onClick={() => setPriceFilter("low")} className={chipCls(priceFilter === "low")} data-testid="abo-filter-price-low">
                {L.priceLow}
              </button>
              <button onClick={() => setPriceFilter("mid")} className={chipCls(priceFilter === "mid")} data-testid="abo-filter-price-mid">
                {L.priceMid}
              </button>
              <button onClick={() => setPriceFilter("high")} className={chipCls(priceFilter === "high")} data-testid="abo-filter-price-high">
                {L.priceHigh}
              </button>
              <span className="text-zinc-500 text-xs uppercase tracking-[0.2em] ml-2" data-testid="abo-results-count">
                {filtered.length} {L.count}
              </span>
            </div>
          </div>

          {/* Fahrzeugkarten */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="abo-offers-grid">
            {filtered.map((o, i) => (
              <motion.article
                key={o.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="group bg-obsidian-surface border border-white/10 hover:border-brand-light/40 transition-colors duration-500 flex flex-col overflow-hidden"
                data-testid={`abo-offer-card-${o.id}`}
              >
                {o.image && (
                  <div className="relative aspect-video overflow-hidden bg-obsidian">
                    <img
                      src={o.image}
                      alt={`${o.brand} ${o.title} – Beispielfoto`}
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                    <span className="absolute bottom-2.5 right-2.5 bg-black/60 backdrop-blur-md text-zinc-400 text-[10px] uppercase tracking-[0.15em] px-2 py-1" data-testid={`abo-offer-example-${o.id}`}>
                      {L.examplePhoto}
                    </span>
                  </div>
                )}
                <div className="p-8 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-brand-bright text-[11px] font-bold uppercase tracking-[0.25em]">{o.brand}</p>
                    <h3 className="font-heading font-black uppercase tracking-tight text-white text-2xl leading-[1.05] mt-2" data-testid={`abo-offer-title-${o.id}`}>
                      {o.title}
                    </h3>
                  </div>
                  <span className="shrink-0 inline-flex items-center gap-1.5 bg-brand/15 border border-brand-light/30 text-brand-bright text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5" data-testid={`abo-offer-badge-${o.id}`}>
                    <Zap className="w-3 h-3" />
                    {L.badge}
                  </span>
                </div>
                <p className="text-zinc-500 text-[11px] uppercase tracking-[0.2em] mt-2">{L.badgeSub}</p>

                <div className="mt-7 pt-6 border-t border-white/10">
                  <p className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-heading text-5xl font-extrabold text-brand-bright leading-none" data-testid={`abo-offer-price-${o.id}`}>
                      {fmtEUR(o.price)}
                    </span>
                    <span className="text-zinc-400 text-sm uppercase tracking-wider">{L.perMonth}</span>
                  </p>
                  <p className="flex items-center gap-2 text-zinc-300 text-sm mt-3" data-testid={`abo-offer-km-${o.id}`}>
                    <Gauge className="w-4 h-4 text-brand-bright shrink-0" />
                    {fmtKm(o.free_km)} {L.freeKm}
                  </p>
                  {o.note && (
                    <p className="text-zinc-500 text-xs uppercase tracking-wider mt-3 border-l-2 border-brand-light/50 pl-3" data-testid={`abo-offer-note-${o.id}`}>
                      {o.note}
                    </p>
                  )}
                </div>

                <button
                  onClick={() => inquire(o)}
                  className="group/btn mt-auto pt-7"
                  data-testid={`abo-offer-cta-${o.id}`}
                >
                  <span className="flex items-center justify-center gap-2 w-full bg-brand text-black font-bold uppercase tracking-wider text-sm px-6 py-4 group-hover/btn:bg-brand-hover transition-colors">
                    {L.cta}
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </span>
                </button>
                </div>
              </motion.article>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-zinc-500 text-sm mt-10" data-testid="abo-empty">{L.empty}</p>
          )}
        </div>
      </section>

      {/* Abo & Vermietung */}
      <section className="px-6 md:px-10 pb-24">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-obsidian-surface border border-white/10 p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-brand/15 border border-brand-light/30"><KeyRound className="w-6 h-6 text-brand-bright" /></div>
              <span className="text-xs uppercase tracking-[0.25em] text-brand-bright">{t.abo.subscription.tag}</span>
            </div>
            <h3 className="font-heading font-extrabold uppercase tracking-tight text-white text-2xl mb-4">{t.abo.subscription.title}</h3>
            <p className="text-zinc-400 leading-relaxed mb-6">{t.abo.subscription.desc}</p>
            <ul className="space-y-3">
              {t.abo.subscription.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-zinc-200"><Check className="w-5 h-5 text-brand-bright shrink-0 mt-0.5" /><span>{f}</span></li>
              ))}
            </ul>
          </div>
          <div className="bg-obsidian-surface border border-white/10 p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-brand/15 border border-brand-light/30"><CalendarClock className="w-6 h-6 text-brand-bright" /></div>
              <span className="text-xs uppercase tracking-[0.25em] text-brand-bright">{t.abo.rental.tag}</span>
            </div>
            <h3 className="font-heading font-extrabold uppercase tracking-tight text-white text-2xl mb-4">{t.abo.rental.title}</h3>
            <p className="text-zinc-400 leading-relaxed mb-6">{t.abo.rental.desc}</p>
            <ul className="space-y-3 mb-8">
              {t.abo.rental.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-zinc-200"><Check className="w-5 h-5 text-brand-bright shrink-0 mt-0.5" /><span>{f}</span></li>
              ))}
            </ul>
            <button onClick={() => { setPrefill(t.abo.rental.title); setTimeout(scrollContact, 60); }} className="group inline-flex items-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-7 py-4 hover:bg-brand-hover transition-colors" data-testid="abo-cta-rental">
              {t.abo.rental.cta}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="px-6 md:px-10 py-20 border-t border-white/10 bg-obsidian-surface" data-testid="abo-testimonials">
        <div className="max-w-[1600px] mx-auto">
          <h2 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-5xl mb-12">{t.abo.testimonialsTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.abo.testimonials.map((tm, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
                className="bg-obsidian border border-white/10 p-7 flex flex-col"
                data-testid={`abo-testimonial-${i}`}
              >
                <Quote className="w-7 h-7 text-brand-bright/40 mb-4" />
                <p className="text-zinc-300 leading-relaxed text-sm flex-1">{tm.text}</p>
                <div className="mt-6 pt-4 border-t border-white/10 font-heading font-bold uppercase tracking-tight text-white text-sm">{tm.name}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 md:px-10 py-20 border-t border-white/10" data-testid="abo-faq">
        <div className="max-w-[900px] mx-auto">
          <h2 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-5xl mb-10">{t.abo.faqTitle}</h2>
          <Accordion type="single" collapsible className="w-full">
            {t.abo.faq.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-white/10" data-testid={`abo-faq-${i}`}>
                <AccordionTrigger className="text-white font-heading font-bold uppercase tracking-tight text-base md:text-lg py-6 hover:no-underline hover:text-brand-bright">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-400 leading-relaxed text-sm md:text-base pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <ContactSection prefillVehicle={prefill} />
      <Footer />
      <WhatsAppButton prefill={`Hallo DNA Automotive, ich interessiere mich für ${t.abo.title}.`} />
    </div>
  );
}
